import type { CompanySettings, OfferDraftRowKind, OfferPositionSurchargeConfig } from '../../../types';
import { isInvoiceFamilyDocumentType } from '../../../utils/documentNumberFormat';
import { formatEuro } from './offerDocumentUtils';
import { contributesToOfferNetTotal, documentTreatsEventualAsNormal, lineTotalCents } from './offerEventualPosition';
import { bakedLaborShareFor35a } from './offerLaborPricing';

export type LaborCostKind = 'leistung' | 'material' | 'neutral';
export type LaborCostShareMode = 'auto' | 'on' | 'off';

export type LaborCostShareSettings = {
  labor_cost_share_offer_private: boolean;
  labor_cost_share_offer_business: boolean;
  labor_cost_share_invoice_private: boolean;
  labor_cost_share_invoice_business: boolean;
};

export const DEFAULT_LABOR_COST_SHARE_SETTINGS: LaborCostShareSettings = {
  labor_cost_share_offer_private: true,
  labor_cost_share_offer_business: false,
  labor_cost_share_invoice_private: true,
  labor_cost_share_invoice_business: false,
};

const LABOR_SHARE_OFFER_LABELS = new Set(['Angebot', 'Kostenvoranschlag', 'Auftragsbestätigung']);

export type LaborCostShareRow = {
  id: string;
  kind: OfferDraftRowKind;
  sourceType?: string | null;
  costKind?: LaborCostKind | null;
  quantity: number;
  unit_price_vk: number;
  labor_minutes?: number;
  hourly_rate?: number | null;
  isEventual?: boolean | null;
  positionSurcharge?: OfferPositionSurchargeConfig | null;
};

export type LaborCostShareAmounts = {
  net: number;
  vat: number;
  gross: number;
};

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function parseBool(raw: unknown, fallback: boolean): boolean {
  if (typeof raw === 'boolean') return raw;
  if (raw === 1 || raw === '1' || raw === 'true') return true;
  if (raw === 0 || raw === '0' || raw === 'false') return false;
  return fallback;
}

export function isLaborCostKind(value: unknown): value is LaborCostKind {
  return value === 'leistung' || value === 'material' || value === 'neutral';
}

export function parseLaborCostShareMode(raw: unknown): LaborCostShareMode {
  if (raw === 'on' || raw === 'off' || raw === 'auto') return raw;
  return 'auto';
}

export function laborCostShareSettingsFromUnknown(raw: unknown): LaborCostShareSettings {
  const row = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    labor_cost_share_offer_private: parseBool(
      row.labor_cost_share_offer_private,
      DEFAULT_LABOR_COST_SHARE_SETTINGS.labor_cost_share_offer_private,
    ),
    labor_cost_share_offer_business: parseBool(
      row.labor_cost_share_offer_business,
      DEFAULT_LABOR_COST_SHARE_SETTINGS.labor_cost_share_offer_business,
    ),
    labor_cost_share_invoice_private: parseBool(
      row.labor_cost_share_invoice_private,
      DEFAULT_LABOR_COST_SHARE_SETTINGS.labor_cost_share_invoice_private,
    ),
    labor_cost_share_invoice_business: parseBool(
      row.labor_cost_share_invoice_business,
      DEFAULT_LABOR_COST_SHARE_SETTINGS.labor_cost_share_invoice_business,
    ),
  };
}

export function laborCostShareSettingsFromCompany(
  company: Pick<
    CompanySettings,
    | 'labor_cost_share_offer_private'
    | 'labor_cost_share_offer_business'
    | 'labor_cost_share_invoice_private'
    | 'labor_cost_share_invoice_business'
  > | null | undefined,
): LaborCostShareSettings {
  return laborCostShareSettingsFromUnknown(company ?? {});
}

export function isLaborCostShareOfferDocument(documentTypeLabel: string): boolean {
  return LABOR_SHARE_OFFER_LABELS.has(String(documentTypeLabel ?? '').trim());
}

export function isLaborCostShareEligibleDocument(documentTypeLabel: string): boolean {
  const label = String(documentTypeLabel ?? '').trim();
  return isLaborCostShareOfferDocument(label) || isInvoiceFamilyDocumentType(label);
}

export function resolveLaborCostKind(row: {
  kind: OfferDraftRowKind;
  sourceType?: string | null;
  costKind?: LaborCostKind | null;
}): LaborCostKind {
  if (row.kind === 'title' || row.kind === 'text' || row.kind === 'image') return 'neutral';
  if (
    (row.kind === 'freePosition' || row.kind === 'positionSurcharge') &&
    isLaborCostKind(row.costKind)
  ) {
    return row.costKind;
  }
  const st = String(row.sourceType ?? '').trim();
  if (st === 'leistung') return 'leistung';
  if (st === 'material') return 'material';
  return 'neutral';
}

/** Freitext always; Zuschlag only when unclassified (no targets) or already overridden. */
export function shouldShowLaborCostKindSwitcher(row: {
  kind: OfferDraftRowKind;
  costKind?: LaborCostKind | null;
  positionSurcharge?: OfferPositionSurchargeConfig | null;
}): boolean {
  if (row.kind === 'freePosition') return true;
  if (row.kind !== 'positionSurcharge') return false;
  if (isLaborCostKind(row.costKind)) return true;
  return (row.positionSurcharge?.targetRowIds ?? []).length === 0;
}

function rowContributes(
  row: LaborCostShareRow,
  treatEventualAsNormal: boolean,
): boolean {
  if (treatEventualAsNormal) {
    if (row.kind === 'title' || row.kind === 'text' || row.kind === 'image') return false;
    return true;
  }
  return contributesToOfferNetTotal(row);
}

export function computeLaborCostNet(
  rows: LaborCostShareRow[],
  documentTypeLabel: string,
  opts?: {
    hourlyRate?: number;
    listLaborSeparately?: boolean;
    partialInvoiceMode?: 'percent' | 'amount' | 'position' | null;
  },
): number {
  const treatEventualAsNormal = documentTreatsEventualAsNormal(documentTypeLabel, {
    partialInvoiceMode: opts?.partialInvoiceMode,
  });
  const byId = new Map(rows.map((r) => [r.id, r]));
  let labor = 0;

  for (const row of rows) {
    if (!rowContributes(row, treatEventualAsNormal)) continue;
    const line = lineTotalCents(row.quantity, row.unit_price_vk);
    if (row.kind === 'positionSurcharge') {
      const explicit = isLaborCostKind(row.costKind) ? row.costKind : null;
      if (explicit === 'leistung') {
        labor += line;
        continue;
      }
      if (explicit === 'material' || explicit === 'neutral') continue;
      const targets = row.positionSurcharge?.targetRowIds ?? [];
      let leistungBase = 0;
      let totalBase = 0;
      for (const tid of targets) {
        const target = byId.get(tid);
        if (!target || !rowContributes(target, treatEventualAsNormal)) continue;
        const tLine = lineTotalCents(target.quantity, target.unit_price_vk);
        totalBase += tLine;
        if (resolveLaborCostKind(target) === 'leistung') leistungBase += tLine;
      }
      if (totalBase > 0) labor += line * (leistungBase / totalBase);
      continue;
    }
    if (resolveLaborCostKind(row) === 'leistung') labor += line;
  }

  const hourlyRate = opts?.hourlyRate ?? 0;
  const listLaborSeparately = opts?.listLaborSeparately === true;
  if (!listLaborSeparately) {
    for (const row of rows) {
      if (!rowContributes(row, treatEventualAsNormal)) continue;
      labor += bakedLaborShareFor35a(row, hourlyRate, listLaborSeparately);
    }
  }

  return round2(labor);
}

export function prorateLaborNet(laborRaw: number, rawNetTotal: number, netTotal: number): number {
  if (!Number.isFinite(laborRaw) || laborRaw <= 0) return 0;
  if (!Number.isFinite(rawNetTotal) || rawNetTotal <= 0) return 0;
  if (!Number.isFinite(netTotal) || netTotal === 0) return 0;
  if (rawNetTotal === netTotal) return round2(laborRaw);
  return round2(laborRaw * (netTotal / rawNetTotal));
}

export function laborCostShareAmounts(laborNet: number, vatRatePercent: number): LaborCostShareAmounts {
  const net = round2(Math.max(0, laborNet));
  const rate = Number.isFinite(vatRatePercent) ? vatRatePercent / 100 : 0;
  const vat = round2(net * rate);
  return { net, vat, gross: round2(net + vat) };
}

export function formatLaborCostShareFootnote(amounts: LaborCostShareAmounts): string {
  return (
    `Im Bruttobetrag sind ${formatEuro(amounts.gross)} (netto ${formatEuro(amounts.net)}) ` +
    `Lohnkosten enthalten. Die darin enthaltene Umsatzsteuer beträgt ${formatEuro(amounts.vat)}.`
  );
}

export function shouldShowLaborCostShare(opts: {
  settings: LaborCostShareSettings;
  mode: LaborCostShareMode;
  isPrivateCustomer: boolean;
  documentTypeLabel: string;
  hideTotals: boolean;
  laborNet: number;
}): boolean {
  if (opts.hideTotals) return false;
  if (!isLaborCostShareEligibleDocument(opts.documentTypeLabel)) return false;
  if (!(opts.laborNet > 0)) return false;
  if (opts.mode === 'off') return false;
  if (opts.mode === 'on') return true;

  const invoice = isInvoiceFamilyDocumentType(opts.documentTypeLabel);
  if (opts.isPrivateCustomer) {
    return invoice ? opts.settings.labor_cost_share_invoice_private : opts.settings.labor_cost_share_offer_private;
  }
  return invoice ? opts.settings.labor_cost_share_invoice_business : opts.settings.labor_cost_share_offer_business;
}

export function resolveLaborCostShareFootnote(opts: {
  rows: LaborCostShareRow[];
  documentTypeLabel: string;
  netTotal: number;
  rawNetTotal: number;
  vatRatePercent: number;
  hideTotals: boolean;
  settings: LaborCostShareSettings;
  mode: LaborCostShareMode;
  isPrivateCustomer: boolean;
  hourlyRate?: number;
  listLaborSeparately?: boolean;
  partialInvoiceMode?: 'percent' | 'amount' | 'position' | null;
}): string | null {
  const laborRaw = computeLaborCostNet(opts.rows, opts.documentTypeLabel, {
    hourlyRate: opts.hourlyRate,
    listLaborSeparately: opts.listLaborSeparately,
    partialInvoiceMode: opts.partialInvoiceMode,
  });
  const laborNet = prorateLaborNet(laborRaw, opts.rawNetTotal, opts.netTotal);
  if (
    !shouldShowLaborCostShare({
      settings: opts.settings,
      mode: opts.mode,
      isPrivateCustomer: opts.isPrivateCustomer,
      documentTypeLabel: opts.documentTypeLabel,
      hideTotals: opts.hideTotals,
      laborNet,
    })
  ) {
    return null;
  }
  return formatLaborCostShareFootnote(laborCostShareAmounts(laborNet, opts.vatRatePercent));
}
